/**
 * Analytics session service — guarantees a session id for backend telemetry.
 *
 * Backend Application Insights custom events only carry a session_Id when the
 * triggering request does. Two gaps leave requests bare: the first API calls of
 * a visit beat the App Insights SDK's `ai_session` cookie write, and the cookie
 * expires (Max-Age <= 30 min) on idle tabs before the SDK renews it. This module
 * resolves an id synchronously before every API call so baseApiService can
 * transmit it explicitly in the X-AI-Session-Id header (NOR-43).
 *
 * Single-minter rule: when the SDK is initialized, only the SDK may mint or
 * renew a session id — `sessionManager.update()` runs its expiry check and
 * renews synchronously, so the id read afterwards is never stale and never
 * competes with an id of our own. The manual cookie path below runs only when
 * the SDK is absent (no instrumentation key); it writes the exact SDK cookie
 * format so the SDK adopts the session when it initializes.
 */

import { getAppInsightsRoot } from './appInsightsService'

export const sessionCookieName = 'ai_session'

// SDK defaults: sessionRenewalMs (sliding) and sessionExpirationMs (absolute cap).
const SESSION_RENEWAL_MS = 30 * 60 * 1000
const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000

// The SDK mints ids via newId(22) over this alphabet; ours must be indistinguishable.
const SESSION_ID_LENGTH = 22
const SESSION_ID_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

// Page-lifetime session used only when cookies cannot be written — minting per
// request would stamp a unique "session" on every event.
let inMemorySession = null

/**
 * Resolve the analytics session id to send as X-AI-Session-Id.
 * Returns null when no id can be resolved. Never throws.
 */
export function ensureAnalyticsSession() {
  try {
    return resolveSessionId()
  } catch (error) {
    return null
  }
}

function resolveSessionId() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null
  }

  const root = getAppInsightsRoot()
  if (root) {
    root.context.sessionManager.update()
    return root.context.getSessionId() || null
  }

  const now = Date.now()
  const existing = parseSessionCookieValue(readSessionCookie())

  if (existing && isLive(existing, now)) {
    const slid = { ...existing, renewalMs: now }
    return writeSessionCookie(slid) ? slid.id : inMemorySessionId(now)
  }

  const minted = { id: newSessionId(), acquisitionMs: now, renewalMs: now }
  return writeSessionCookie(minted) ? minted.id : inMemorySessionId(now)
}

/**
 * Parse an `ai_session` cookie value (`{id}|{acquisitionMs}|{renewalMs}`).
 * Mirrors the SDK's adoption rules: id non-empty, both timestamps positive numbers.
 */
export function parseSessionCookieValue(value) {
  if (!value) {
    return null
  }

  const tokens = value.split('|')
  if (tokens.length < 3) {
    return null
  }

  const acquisitionMs = Number(tokens[1])
  const renewalMs = Number(tokens[2])

  if (!tokens[0] || !(acquisitionMs > 0) || !(renewalMs > 0)) {
    return null
  }

  return { id: tokens[0], acquisitionMs, renewalMs }
}

function isLive(session, nowMs) {
  return (
    nowMs - session.renewalMs < SESSION_RENEWAL_MS &&
    nowMs - session.acquisitionMs < SESSION_EXPIRY_MS
  )
}

function readSessionCookie() {
  const match = document.cookie.match('(^|;)\\s*' + sessionCookieName + '\\s*=\\s*([^;]+)')
  return match ? match.pop() : undefined
}

// Write the cookie with the SDK's attributes (path=/, Max-Age = renewal window
// capped by the remaining 24 h expiry, Secure on https, SameSite=Lax).
// Returns false when the write did not stick (cookies disabled/blocked).
function writeSessionCookie(session) {
  const value = `${session.id}|${session.acquisitionMs}|${session.renewalMs}`
  const remainingMs = session.acquisitionMs + SESSION_EXPIRY_MS - session.renewalMs
  const maxAgeSeconds = Math.max(0, Math.min(SESSION_RENEWAL_MS, remainingMs)) / 1000

  let cookie = `${sessionCookieName}=${value};path=/;max-age=${Math.floor(maxAgeSeconds)};SameSite=Lax`
  if (window.location.protocol === 'https:') {
    cookie += ';Secure'
  }

  document.cookie = cookie

  const readBack = readSessionCookie()
  return readBack !== undefined && readBack.split('|')[0] === session.id
}

function inMemorySessionId(nowMs) {
  if (inMemorySession && isLive(inMemorySession, nowMs)) {
    inMemorySession.renewalMs = nowMs
  } else {
    inMemorySession = { id: newSessionId(), acquisitionMs: nowMs, renewalMs: nowMs }
  }
  return inMemorySession.id
}

function newSessionId() {
  const chars = []
  const randomValues = new Uint8Array(SESSION_ID_LENGTH)

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(randomValues)
  } else {
    for (let i = 0; i < SESSION_ID_LENGTH; i++) {
      randomValues[i] = Math.floor(Math.random() * 256)
    }
  }

  for (let i = 0; i < SESSION_ID_LENGTH; i++) {
    chars.push(SESSION_ID_ALPHABET[randomValues[i] % SESSION_ID_ALPHABET.length])
  }

  return chars.join('')
}
